import { apiClient } from '@/api/client';
import {
  Business,
  PathsAdminBusinessesGetParametersQueryStatus,
} from '@/api/models';
import { Button } from '@/components/ui/Button';
import { FormErrorMessage } from '@/components/ui/FormErrorMessage';
import { useDebounce } from '@/hooks/useDebounce';
import { queryKeys } from '@/lib/queryKeys';
import { PlanFormData } from '@/pages/users/hooks/useUserPlanMutations';
import { mdiCalendar, mdiCheck, mdiClose } from '@mdi/js';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';

interface UserPlanFormProps {
  onSubmit: (data: PlanFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

interface FormValues {
  businessId: string;
  startAt: string;
  endAt: string;
}

const formatDateForInput = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  // Format as local datetime-local value
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const getDefaultStartDate = (): string => {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 30);
  now.setMinutes(0);
  now.setSeconds(0);
  return formatDateForInput(now);
};

const getDefaultEndDate = (): string => {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 30);
  now.setMinutes(0);
  now.setSeconds(0);
  now.setHours(now.getHours() + 2);
  return formatDateForInput(now);
};

const getDefaultFormValues = (): FormValues => ({
  businessId: '',
  startAt: getDefaultStartDate(),
  endAt: getDefaultEndDate(),
});

export const UserPlanForm: React.FC<UserPlanFormProps> = ({
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [businessSearch, setBusinessSearch] = React.useState('');
  const debouncedBusinessSearch = useDebounce(businessSearch, 300);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = useForm<FormValues>({
    defaultValues: getDefaultFormValues(),
    mode: 'onChange',
  });

  const selectedBusinessId = watch('businessId');
  const startAt = watch('startAt');

  // Fetch active businesses for dropdown
  const { data: businessesData, isLoading: isLoadingBusinesses } = useQuery({
    queryKey: [queryKeys.ADMIN_BUSINESSES, 'active', debouncedBusinessSearch],
    queryFn: async () => {
      const response = await apiClient.GET('/admin/businesses', {
        params: {
          query: {
            page: 1,
            limit: 50,
            status: PathsAdminBusinessesGetParametersQueryStatus.Active,
            search: debouncedBusinessSearch || undefined,
            sortBy: 'name',
            sortOrder: 'asc',
          },
        },
      });

      if (response.error) {
        throw new Error('Failed to fetch businesses');
      }

      return response.data || [];
    },
  });

  const businesses: Business[] = businessesData || [];

  // Find selected business for display
  const selectedBusiness = businesses.find(
    b => b.id?.toString() === selectedBusinessId,
  );

  const handleFormSubmit = async (data: FormValues) => {
    const startDate = new Date(data.startAt);
    const endDate = new Date(data.endAt);

    await onSubmit({
      businessId: parseInt(data.businessId),
      startAt: startDate.toISOString(),
      endAt: endDate.toISOString(),
    });
  };

  // Validate dates
  const validateEndDate = (endAt: string) => {
    if (!startAt) return true;
    const start = new Date(startAt);
    const end = new Date(endAt);
    if (end <= start) {
      return 'End date must be after start date';
    }
    const diffHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    if (diffHours > 24) {
      return 'Plan duration cannot exceed 24 hours';
    }
    return true;
  };

  const validateStartDate = (startAtVal: string) => {
    const start = new Date(startAtVal);
    const now = new Date();
    now.setMinutes(now.getMinutes() - 5);
    if (start < now) {
      return 'Start date cannot be in the past';
    }
    return true;
  };

  return (
    <div className='p-6'>
      <div className='flex items-center space-x-3 mb-6 pb-4 border-b border-grey-200'>
        <div className='w-12 h-12 bg-grey-100 rounded-full flex items-center justify-center'>
          <div className='w-6 h-6 text-grey-600'>
            <svg viewBox='0 0 24 24' fill='currentColor'>
              <path d={mdiCalendar} />
            </svg>
          </div>
        </div>
        <div>
          <h3 className='text-lg font-semibold text-grey-900'>
            Create New Plan
          </h3>
          <p className='text-sm text-grey-600'>
            Schedule a new plan at a business
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(handleFormSubmit)} className='space-y-6'>
        {/* Business Selection */}
        <div className='bg-grey-50 rounded-lg p-4'>
          <h4 className='text-sm font-medium text-grey-700 mb-4'>Business</h4>

          {/* Search Input */}
          <div className='mb-3'>
            <input
              type='text'
              placeholder='Search businesses...'
              value={businessSearch}
              onChange={e => setBusinessSearch(e.target.value)}
              className='w-full px-3 py-2 border border-grey-300 rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm'
            />
          </div>

          {/* Business Dropdown */}
          <Controller
            name='businessId'
            control={control}
            rules={{
              required: 'Please select a business',
            }}
            render={({ field }) => (
              <select
                {...field}
                className={`w-full px-3 py-2 border rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.businessId ? 'border-red-300' : 'border-grey-300'
                }`}
                disabled={isLoadingBusinesses}
              >
                <option value=''>
                  {isLoadingBusinesses
                    ? 'Loading businesses...'
                    : 'Select a business'}
                </option>
                {businesses.map(business => (
                  <option key={business.id} value={business.id?.toString()}>
                    {business.name} - {business.city}
                  </option>
                ))}
              </select>
            )}
          />
          <FormErrorMessage message={errors.businessId?.message} />

          {/* Selected Business Info */}
          {selectedBusiness && (
            <div className='mt-3 p-3 bg-white rounded border border-grey-200'>
              <p className='font-medium text-grey-900'>
                {selectedBusiness.name}
              </p>
              <p className='text-sm text-grey-500'>
                {selectedBusiness.address}, {selectedBusiness.city}
              </p>
            </div>
          )}
        </div>

        {/* Date/Time Selection */}
        <div className='bg-grey-50 rounded-lg p-4'>
          <h4 className='text-sm font-medium text-grey-700 mb-4'>Schedule</h4>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <label className='block text-sm font-medium text-grey-700 mb-1'>
                Start Date & Time *
              </label>
              <Controller
                name='startAt'
                control={control}
                rules={{
                  required: 'Start date is required',
                  validate: validateStartDate,
                }}
                render={({ field }) => (
                  <input
                    {...field}
                    type='datetime-local'
                    className={`w-full px-3 py-2 border rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.startAt ? 'border-red-300' : 'border-grey-300'
                    }`}
                  />
                )}
              />
              <FormErrorMessage message={errors.startAt?.message} />
            </div>

            <div>
              <label className='block text-sm font-medium text-grey-700 mb-1'>
                End Date & Time *
              </label>
              <Controller
                name='endAt'
                control={control}
                rules={{
                  required: 'End date is required',
                  validate: validateEndDate,
                }}
                render={({ field }) => (
                  <input
                    {...field}
                    type='datetime-local'
                    className={`w-full px-3 py-2 border rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.endAt ? 'border-red-300' : 'border-grey-300'
                    }`}
                  />
                )}
              />
              <FormErrorMessage message={errors.endAt?.message} />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className='flex items-center justify-end space-x-3 pt-6 border-t border-grey-200'>
          <Button
            type='button'
            label='Cancel'
            icon={mdiClose}
            color='light'
            onClick={onCancel}
            disabled={loading}
          />
          <Button
            type='submit'
            label='Create Plan'
            icon={mdiCheck}
            color='primary'
            loading={loading}
            disabled={!isValid}
          />
        </div>
      </form>
    </div>
  );
};
